import { useEffect, useState, type ReactNode } from "react";
import { Link, useNavigate, useParams, useRouterState } from "@tanstack/react-router";
import { BrandLockup, BrandMark } from "./BrandMark";
import { Button } from "./ui/Button";
import { StatusDot } from "./ui/Chip";
import { ConfirmDialog } from "./ui/ConfirmDialog";
import {
  BookIcon,
  CalendarIcon,
  ChartIcon,
  ChatIcon,
  CheckIcon,
  ChevronRightIcon,
  CodeIcon,
  CpuIcon,
  DashboardIcon,
  DatabaseIcon,
  GaugeIcon,
  HomeIcon,
  NoteIcon,
  PlusIcon,
  ReportIcon,
  SearchIcon,
  ShieldIcon,
  SidebarIcon,
  SparkIcon,
  TrashIcon,
  UsersIcon,
} from "./icons";
import { useSidebar } from "./sidebarState";
import { ViewerMenu } from "./ViewerMenu";
import { useDeleteThread, useIsAdmin, useMeta, useThreads } from "@/api/queries";
import { useNewConversation } from "@/chat/newConversation";
import { cn } from "@/lib/cn";

/**
 * The one left drawer (backlog items 1 and 2).
 *
 * There used to be two: this nav, and the chat page's own rail carrying the
 * source picker and the conversation list. Side by side they spent 544px
 * before the conversation began. They are one drawer now — nav at the top,
 * conversations below it in the same scroller, the way the desktop app does
 * it — and that drawer collapses two ways.
 *
 * Per-group, for the reader who lives in Workspace and rarely opens Admin;
 * and whole, down to an icon rail, for the reader who wants the screen back.
 * Both survive a reload, because a nav that re-opens itself on every route
 * change is a nav that is arguing with you.
 *
 * The rail drops the conversation list rather than shrinking it: a column of
 * identical chat glyphs is not a list of conversations, it is a wall. Clicking
 * Conversations there navigates, and the list is a keystroke away.
 */

interface NavItem {
  label: string;
  icon: ReactNode;
  /** Set once the section ships; until then the item is inert. */
  to?:
    | "/"
    | "/sources"
    | "/chat"
    | "/workbench"
    | "/guide"
    | "/reports"
    | "/dashboards"
    | "/settings/providers"
    | "/settings/prompts"
    | "/settings/schedules"
    | "/settings/usage"
    | "/settings/theming"
    | "/settings/audit"
    | "/settings/users"
    | "/settings/evals"
    | "/settings/operations";
  /** Milestone that turns this section on — shown until it ships. */
  arriving?: string;
}

const NAV: Array<{ heading: string; items: NavItem[]; adminOnly?: boolean }> = [
  {
    heading: "Workspace",
    items: [
      { label: "Home", icon: <HomeIcon className="size-4" />, to: "/" },
      { label: "Conversations", icon: <ChatIcon className="size-4" />, to: "/chat" },
      { label: "Reports", icon: <ReportIcon className="size-4" />, to: "/reports" },
      { label: "Dashboards", icon: <DashboardIcon className="size-4" />, to: "/dashboards" },
      { label: "SQL workbench", icon: <CodeIcon className="size-4" />, to: "/workbench" },
    ],
  },
  {
    heading: "Deployment",
    items: [
      { label: "Sources", icon: <DatabaseIcon className="size-4" />, to: "/sources" },
      { label: "Schedules", icon: <CalendarIcon className="size-4" />, to: "/settings/schedules" },
    ],
  },
  // The Admin tier's surfaces (ACC-2). Grouping them is what made that a
  // permission on one heading rather than an audit of seven scattered links.
  // Hidden from an analyst; the API refuses them regardless.
  {
    heading: "Admin",
    adminOnly: true,
    items: [
      { label: "Models", icon: <CpuIcon className="size-4" />, to: "/settings/providers" },
      { label: "Prompt", icon: <NoteIcon className="size-4" />, to: "/settings/prompts" },
      { label: "Evals", icon: <CheckIcon className="size-4" />, to: "/settings/evals" },
      { label: "Usage", icon: <ChartIcon className="size-4" />, to: "/settings/usage" },
      { label: "Audit", icon: <ShieldIcon className="size-4" />, to: "/settings/audit" },
      { label: "People", icon: <UsersIcon className="size-4" />, to: "/settings/users" },
      { label: "Branding", icon: <SparkIcon className="size-4" />, to: "/settings/theming" },
      {
        label: "Operations",
        icon: <GaugeIcon className="size-4" />,
        to: "/settings/operations",
      },
    ],
  },
  {
    heading: "Help",
    items: [{ label: "User guide", icon: <BookIcon className="size-4" />, to: "/guide" }],
  },
];

const ITEM_BASE =
  "type-label text-ink-secondary hover:bg-hover hover:text-ink rounded-element motion-fast transition-colors";
const ITEM_ACTIVE = "bg-selected text-ink";

/** Used only to mark a *folded* group as holding the current page; the links
 *  themselves still style through the router's own `activeProps`. */
function holdsPath(pathname: string, to: string): boolean {
  if (to === "/") return pathname === "/";
  return pathname === to || pathname.startsWith(`${to}/`);
}

function ago(iso: string | null | undefined): string {
  if (!iso) return "";
  const seconds = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (seconds < 60) return "just now";
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86_400) return `${Math.floor(seconds / 3600)}h ago`;
  return `${Math.floor(seconds / 86_400)}d ago`;
}

function isMac(): boolean {
  if (typeof navigator === "undefined") return false;
  return /Mac|iP(hone|ad|od)/.test(navigator.userAgent);
}

/* ---- The conversation list, now a section of the nav rather than a drawer -- */

function Conversations() {
  const [search, setSearch] = useState("");
  // The whole row, not just its id: the dialog names what it is about to
  // delete, and the row can leave the list the moment the delete succeeds.
  const [confirming, setConfirming] = useState<{ id: string; title: string } | null>(null);
  const { data: threads } = useThreads(search);
  const deleteThread = useDeleteThread();
  const navigate = useNavigate();
  const params = useParams({ strict: false }) as { threadId?: string };
  const activeId = params.threadId ?? null;
  const list = threads ?? [];

  return (
    <section className="mt-1 flex flex-col">
      <div className="flex items-center gap-1.5 px-2 pb-1.5">
        <h2 className="type-caption text-ink-disabled flex-1 truncate">Conversations</h2>
        <span className="type-caption text-ink-disabled">{list.length}</span>
      </div>

      <div className="relative px-2 pb-2">
        <SearchIcon className="text-ink-disabled absolute start-4 top-1/2 size-3.5 -translate-y-1/2" />
        <input
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search conversations"
          aria-label="Search conversations"
          className={cn(
            "control-sm rounded-element bg-card text-ink type-support w-full",
            "border-border hover:border-border-emphasized focus:border-accent border ps-8 pe-2",
            "placeholder:text-ink-disabled motion-fast transition-[border-color] outline-none",
          )}
        />
      </div>

      {list.length === 0 ? (
        <p className="type-support text-ink-disabled px-3 py-4 text-center">
          {search ? "Nothing matches that." : "No conversations yet."}
        </p>
      ) : (
        <ul className="flex flex-col gap-0.5">
          {list.map((thread) => (
            <li key={thread.id} className="group relative">
              <Link
                to="/chat/$threadId"
                params={{ threadId: thread.id }}
                className={cn(
                  "rounded-element flex flex-col gap-0.5 px-2 py-1.5 pe-8",
                  "hover:bg-hover motion-fast transition-colors",
                  thread.id === activeId && "bg-selected",
                )}
              >
                <span className="type-label text-ink flex items-center gap-2">
                  <ChatIcon className="text-ink-disabled size-3.5 shrink-0" />
                  <span className="truncate">{thread.title || "New conversation"}</span>
                </span>
                <span className="type-caption text-ink-disabled ps-5.5 truncate">
                  {thread.connection_name ?? "no source"}
                  {/* A conversation across several sources says so (MS-4). */}
                  {(thread.connection_ids?.length ?? 0) > 1
                    ? ` + ${(thread.connection_ids?.length ?? 1) - 1}`
                    : ""}{" "}
                  · {ago(thread.last_message_at)}
                </span>
              </Link>
              <button
                type="button"
                aria-label={`Delete ${thread.title || "this conversation"}`}
                onClick={() =>
                  setConfirming({ id: thread.id, title: thread.title || "New conversation" })
                }
                className={cn(
                  "rounded-inner motion-fast absolute end-1.5 top-1.5 p-1.5 transition-opacity",
                  "opacity-0 group-hover:opacity-100 focus-visible:opacity-100",
                  "text-ink-disabled hover:text-error hover:bg-hover",
                )}
              >
                <TrashIcon className="size-3.5" />
              </button>
            </li>
          ))}
        </ul>
      )}

      {/* One dialog for the list rather than one per row. The armed-trash
          pattern it replaces could not say which conversation was armed on a
          list of near-identical rows, and could not say that the trace and the
          answers go too. */}
      <ConfirmDialog
        open={confirming !== null}
        onClose={() => setConfirming(null)}
        loading={deleteThread.isPending}
        title="Delete this conversation?"
        description={<>“{confirming?.title}” will be deleted.</>}
        consequence="Its questions, answers and query trace go with it. Reports and dashboards saved from it are copies and are not touched."
        onConfirm={() => {
          const target = confirming;
          if (!target) return;
          // `mutate` rather than `mutateAsync`: a rejection here used to be an
          // unhandled one in the console and a conversation that stayed in the
          // list — the 204 bug's exact shape (backlog item 11). The mutation
          // cache announces it now, and the dialog stays open to be retried.
          deleteThread.mutate(target.id, {
            onSuccess: () => {
              setConfirming(null);
              if (target.id === activeId) void navigate({ to: "/chat" });
            },
          });
        }}
      />
    </section>
  );
}

/* ---- The drawer ---------------------------------------------------------- */

export function Sidebar() {
  const { data: meta } = useMeta();
  const isAdmin = useIsAdmin();
  const collapsed = useSidebar((state) => state.collapsed);
  const closed = useSidebar((state) => state.closed);
  const toggle = useSidebar((state) => state.toggle);
  const setCollapsed = useSidebar((state) => state.setCollapsed);
  const toggleGroup = useSidebar((state) => state.toggleGroup);
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const { sources, connectionId, select, start, creating } = useNewConversation();

  const verified = sources.filter((source) => source.last_test_ok).length;
  const summary =
    sources.length > 0
      ? `${sources.length} source${sources.length === 1 ? "" : "s"} · ${verified} verified`
      : "No sources";

  const shortcut = isMac() ? "⌘B" : "Ctrl+B";
  const toggleLabel = collapsed ? "Expand sidebar" : "Collapse sidebar";

  // The accelerator every editor already uses for this, so nobody has to learn it.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key.toLowerCase() !== "b") return;
      if (!(event.metaKey || event.ctrlKey) || event.altKey || event.shiftKey) return;
      event.preventDefault();
      toggle();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [toggle]);

  return (
    <aside
      aria-label="Primary"
      className={cn(
        "border-border bg-surface hidden shrink-0 flex-col overflow-hidden border-e md:flex",
        "motion-medium transition-[width]",
        collapsed ? "w-16" : "w-72",
      )}
    >
      <div
        className={cn(
          "flex h-16 shrink-0 items-center",
          collapsed ? "justify-center px-3" : "gap-2 px-4",
        )}
      >
        {collapsed ? (
          // The mark *is* the expand control: one target, and the glyph only
          // shows itself when you reach for it.
          <button
            type="button"
            onClick={toggle}
            aria-expanded={false}
            aria-controls="sidebar-body"
            title={`${toggleLabel} (${shortcut})`}
            className="group text-ink-secondary hover:bg-hover hover:text-ink rounded-element motion-fast relative flex size-10 items-center justify-center transition-colors"
          >
            <span className="motion-fast transition-opacity group-hover:opacity-0 group-focus-visible:opacity-0">
              <BrandMark size={26} />
            </span>
            <SidebarIcon className="motion-fast absolute size-4 opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100" />
            <span className="sr-only">{toggleLabel}</span>
          </button>
        ) : (
          <>
            <Link to="/" className="rounded-element min-w-0 flex-1">
              <BrandLockup size={28} />
            </Link>
            <button
              type="button"
              onClick={toggle}
              aria-expanded
              aria-controls="sidebar-body"
              title={`${toggleLabel} (${shortcut})`}
              className="text-ink-disabled hover:bg-hover hover:text-ink rounded-element motion-fast flex size-8 shrink-0 items-center justify-center transition-colors"
            >
              <SidebarIcon className="size-4" />
              <span className="sr-only">{toggleLabel}</span>
            </button>
          </>
        )}
      </div>

      {/* The primary action, and the one question it needs answered. */}
      <div className={cn("shrink-0", collapsed ? "px-3 pb-2" : "flex flex-col gap-2 px-3 pb-3")}>
        {collapsed ? (
          <button
            type="button"
            onClick={() => void start(connectionId)}
            disabled={!connectionId || creating}
            title={`New conversation — ${sources.find((s) => s.id === connectionId)?.name ?? "no source"}`}
            className="bg-accent text-on-accent hover:bg-accent-hover rounded-element motion-fast mx-auto flex size-10 items-center justify-center transition-colors disabled:opacity-50"
          >
            <PlusIcon className="size-4" />
            <span className="sr-only">New conversation</span>
          </button>
        ) : (
          <>
            <Button
              variant="primary"
              size="sm"
              className="w-full"
              onClick={() => void start(connectionId)}
              loading={creating}
              disabled={!connectionId}
              icon={<PlusIcon className="size-3.5" />}
            >
              New conversation
            </Button>
            <label className="flex items-center gap-2">
              <span className="type-caption text-ink-disabled shrink-0">Source</span>
              <select
                value={connectionId}
                onChange={(event) => select(event.target.value)}
                aria-label="Source for the next conversation"
                className={cn(
                  "control-sm rounded-element bg-card text-ink type-support min-w-0 flex-1 px-2",
                  "border-border hover:border-border-emphasized focus:border-accent border",
                  "motion-fast transition-[border-color] outline-none",
                )}
              >
                {sources.length === 0 ? <option value="">No sources yet</option> : null}
                {sources.map((source) => (
                  <option key={source.id} value={source.id}>
                    {source.name}
                  </option>
                ))}
              </select>
            </label>
          </>
        )}
      </div>

      {/* One scroller: nav, then conversations. They scroll together on
          purpose — two nested scroll regions in a 288px column is the thing
          this change exists to delete. */}
      <div id="sidebar-body" className="min-h-0 flex-1 overflow-y-auto px-3 pb-4">
        {NAV.filter((group) => isAdmin || !group.adminOnly).map((group, index) => {
          const open = collapsed || !closed.includes(group.heading);
          const holdsActive = group.items.some((item) => item.to && holdsPath(pathname, item.to));

          return (
            <div
              key={group.heading}
              className={cn(
                collapsed ? "py-2" : "mb-3",
                collapsed && index > 0 && "border-border border-t",
              )}
            >
              {collapsed ? null : (
                <button
                  type="button"
                  onClick={() => toggleGroup(group.heading)}
                  aria-expanded={open}
                  className="type-caption text-ink-disabled hover:text-ink-secondary rounded-element motion-fast flex w-full items-center gap-1.5 px-2 py-1.5 transition-colors"
                >
                  <ChevronRightIcon
                    className={cn(
                      "motion-medium size-3 shrink-0 transition-transform",
                      open && "rotate-90",
                    )}
                  />
                  <span className="flex-1 truncate text-start">{group.heading}</span>
                  {!open && holdsActive ? (
                    // A folded group that holds the current page still says so.
                    <span className="bg-accent size-1.5 shrink-0 rounded-full" aria-hidden />
                  ) : null}
                </button>
              )}

              {open ? (
                <ul className="flex flex-col gap-0.5">
                  {group.items.map((item) => (
                    <li key={item.label}>
                      {item.to ? (
                        <Link
                          to={item.to}
                          title={collapsed ? item.label : undefined}
                          aria-label={collapsed ? item.label : undefined}
                          activeOptions={{ exact: item.to === "/" }}
                          className={cn(
                            ITEM_BASE,
                            collapsed
                              ? "mx-auto flex size-10 items-center justify-center"
                              : "flex items-center gap-3 px-2 py-2",
                          )}
                          activeProps={{ className: ITEM_ACTIVE }}
                        >
                          <span className="shrink-0">{item.icon}</span>
                          {collapsed ? null : <span className="flex-1 truncate">{item.label}</span>}
                        </Link>
                      ) : (
                        <span
                          title={collapsed ? item.label : undefined}
                          className={cn(
                            "type-label text-ink-disabled rounded-element cursor-default select-none",
                            collapsed
                              ? "mx-auto flex size-10 items-center justify-center"
                              : "flex items-center gap-3 px-2 py-2",
                          )}
                        >
                          <span className="shrink-0">{item.icon}</span>
                          {collapsed ? null : (
                            <>
                              <span className="flex-1 truncate">{item.label}</span>
                              {item.arriving ? (
                                <span className="type-caption text-ink-disabled">
                                  {item.arriving}
                                </span>
                              ) : null}
                            </>
                          )}
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
          );
        })}

        {collapsed ? null : <Conversations />}
      </div>

      {/* Deployment status, version, and the viewer's own three controls —
          which arrived here from the deleted top bar (backlog item 13). They
          are a menu because this footer is 256px wide in one life and 40px in
          the other; see `ViewerMenu`. */}
      <footer className={cn("border-border shrink-0 border-t py-3", collapsed ? "px-3" : "px-4")}>
        {collapsed ? (
          <div className="flex flex-col items-center gap-1">
            <button
              type="button"
              onClick={() => setCollapsed(false)}
              title={summary}
              className="hover:bg-hover rounded-element motion-fast flex w-full justify-center py-1 transition-colors"
            >
              <StatusDot tone={sources.length > 0 ? "success" : "warning"} />
              <span className="sr-only">{summary}</span>
            </button>
            <ViewerMenu align="start" />
          </div>
        ) : (
          <>
            <div className="flex items-center gap-2">
              <StatusDot tone={sources.length > 0 ? "success" : "warning"} />
              <span className="type-support text-ink-secondary min-w-0 flex-1 truncate">
                {summary}
              </span>
              <ViewerMenu />
            </div>
            {meta ? (
              <p className="type-caption text-ink-disabled mt-1.5">
                v{meta.version} · tokens {meta.theme_version}
              </p>
            ) : null}
          </>
        )}
      </footer>
    </aside>
  );
}
